/*
 * The reply agent's offer to the reply box beside its draft (Dawid's pickers: ledger `87f82a84`,
 * hints and ending in the box; `72427ecc`, the pending-answer warning). The box shows these only
 * while it holds this draft (`inbox-v2/draft-aids.tsx`).
 *
 * "Change ending" saves the box's words with another saved sign-off in one draft request
 * (`saveEnding`), holding the box frozen, as an approval does, until the saved revision is read back
 * and put in whole into the box this conversation has open (`ReplyBoxLink`), even one opened again
 * while the save ran. A refusal is the page's own words, and the plan is read again.
 */
import type { z } from "zod";
import { openQuestions } from "../../src/inbox-view.ts";
import { type DraftRevision, draftEmail } from "../../src/mail-content.ts";
import type { DraftAids } from "../inbox-v2/draft-aids.tsx";
import type { ReplyBoxLink } from "../inbox-v2/reply-box-link.ts";
import { endingIntact, saveEnding } from "./approve-from-box.ts";
import { newestPipelineDraft, parseEmail } from "./conversation-shared.ts";
import type { DetailSchema } from "./detail-schema.ts";

type Detail = z.infer<typeof DetailSchema>;

export function draftAids(input: {
  detail: Detail;
  draft: DraftRevision;
  planApi: string;
  link: ReplyBoxLink;
  /** Reads the plan again and returns it, for the saved revision. */
  refresh: () => Promise<Detail>;
  /** Reads the plan again after a refusal, as Approve does. */
  onChange: () => Promise<void>;
}): DraftAids {
  const { detail, draft, link } = input;
  const { plan } = detail;
  const questions = openQuestions(plan);
  const [first] = questions;
  return {
    ending: draft.signOff
      ? {
          mode: draft.signOff.mode,
          plainText: draft.signOff.plainText,
          options: detail.signOffOptions,
        }
      : null,
    waitingOn: first ? { first: first.prompt, more: questions.length - 1 } : null,
    endingIntact: (box) => endingIntact(box, draft, parseEmail),
    changeEnding: async (mode) => {
      const step = plan.steps.findLast((candidate) => candidate.input.kind === "reply");
      if (!step) throw new Error("This conversation has no reply to change.");
      const pending = link.begin(plan.id);
      try {
        if (!pending.content) throw new Error("Open the reply box on this draft first.");
        const revision = await saveEnding({
          planApi: input.planApi,
          plan,
          step,
          draft,
          box: pending.content,
          mode,
          parse: parseEmail,
        });
        const fresh = await input.refresh();
        const next = newestPipelineDraft(fresh.plan, fresh.drafts);
        if (next?.revision !== revision)
          throw new Error("The draft changed meanwhile. Use Edit in reply box to load it.");
        pending.replaced({ revision, ...draftEmail(next) });
      } catch (cause) {
        await input.onChange();
        throw cause;
      } finally {
        pending.release();
      }
    },
  };
}
